<template>
    <div
        class="ds-number"
        :class="{
            'has-error': error,
            'disabled': disabled,
            'is-active': active,
            ...cssClass,
        }"
    >
        <button
            class="ds-btn ds-btn--primary"
            :disabled="disabled || (modelValue !== '' && Number(modelValue) <= minValue)"
            type="button"
            aria-label="Reduce"
            @click="value = parseNumber(value) - step"
            @focus="active = true"
            @blur="active = false"
        >
            <DSFontIcon code="minus" icon-style="line" />
        </button>
        <div class="label">
            <input
                ref="numberInput"
                type="number"
                :min="minValue"
                :max="maxValue"
                :step="step"
                :value="value"
                :disabled="disabled"
                @input="value = $event.target.value"
                @focus="active = true"
                @blur="active = false"
            />
        </div>
        <button
            class="ds-btn ds-btn--primary"
            :disabled="disabled || (modelValue !== '' && Number(modelValue) >= maxValue)"
            type="button"
            aria-label="Increase"
            @click="value = parseNumber(value) + step"
            @focus="active = true"
            @blur="active = false"
        >
            <DSFontIcon code="plus" icon-set="st1website" />
        </button>
    </div>
</template>
<script setup>
import { ref, computed, useNuxtApp } from '#imports';

const { abs, ceil, floor, max } = Math;
const { $dsc } = useNuxtApp();
const MINIMAL_NUMBER_OF_STEPS = 2;

const props = defineProps({
    cssClass: {
        type: [String, Array, Object],
        required: false,
        default: undefined,
    },
    minValue: {
        type: Number,
        required: false,
        default: 0,
        validator: v => Number.isInteger(v),
    },
    maxValue: {
        type: Number,
        required: false,
        default: 1000,
        validator: v => Number.isInteger(v),
    },
    step: {
        type: Number,
        required: false,
        default: 1,
        validator: v => Number.isInteger(v),
    },
    modelValue: {
        type: [String, Number],
        default: 0,
    },
    error: {
        type: [String],
        required: false,
        default: null,
    },
    disabled: {
        type: Boolean,
        required: false,
        default: false,
    },
});

if (!$dsc.production) {
    if (props.maxValue <= props.minValue) console.warn('DSNumber: min-value must be smaller than max-value.');

    const numberOfSteps = abs(floor(props.maxValue) - ceil(props.minValue)) / props.step;

    if (numberOfSteps < MINIMAL_NUMBER_OF_STEPS) console.warn('DSNumber: step is possibly incorrect.');
}

const emit = defineEmits([ 'update:modelValue' ]);

const numberInput = ref(null);
const active = ref(false);

const crossBrowserWidth = computed(() => {
    const minus = props.maxValue < 0 || props.minValue < 0 ? 1 : 0;

    const getMaxDigits = v => String(v).length;
    const maxDigits = max(getMaxDigits(props.minValue), getMaxDigits(props.maxValue));
    const maxChars = maxDigits + minus;
    const paddingChars = 2;

    return `${maxChars + paddingChars}ch`;
});

const parseNumber = n => Number(n.replace(/-\D/g, ''));

const value = computed({
    get: () => String(props.modelValue),
    set: v => {
        if (['', '-'].includes(numberInput.value?.value)) return;

        const limitedValue = v > props.maxValue
            ? props.maxValue
            : v < props.minValue ? props.minValue : v;

        (numberInput.value).value = limitedValue;
        emit('update:modelValue', limitedValue);
    },
});
</script>

<style scoped>
input {
  width: v-bind("crossBrowserWidth");
  min-width: 100%;
}
</style>
